// @ts-nocheck
import * as React from "react";
import * as V from "../api";
import {
  Badge, Button, Card, Empty, Field, Input, Loading, Modal, Select, TOKENS, Textarea,
  errorMessage, useToast,
} from "../ui";

// Phase 4 — 목민형 마을교사 인증(아젠다 ②). 신청·조회·승인·정지.

export function TeachersPanel({ bureaus, isOperator }: { bureaus: V.Bureau[]; isOperator: boolean }) {
  const toast = useToast();
  const [status, setStatus] = React.useState("");
  const [list, setList] = React.useState<V.Teacher[] | null>(null);
  const [applying, setApplying] = React.useState(false);

  const reload = React.useCallback(() => {
    V.teachers.list({ status: status || undefined })
      .then((r) => setList(r.items))
      .catch((e) => { setList([]); toast.push(errorMessage(e), "danger"); });
  }, [status, toast]);
  React.useEffect(() => { reload(); }, [reload]);

  const act = async (fn: () => Promise<any>, msg: string) => {
    try { await fn(); toast.push(msg, "success"); reload(); }
    catch (e: any) { toast.push(errorMessage(e), "danger"); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
          마을교사 인증
          <span style={{ fontSize: 12, color: TOKENS.ink3, marginLeft: 8, fontWeight: 500 }}>
            {isOperator ? "(전체)" : "(내 인증만)"}
          </span>
        </h2>
        <div style={{ display: "flex", gap: 6 }}>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 120 }}>
            <option value="">전체</option>
            {Object.entries(V.LABELS.teacherStatus).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </Select>
          <Button onClick={() => setApplying(true)}>인증 신청</Button>
        </div>
      </div>

      {list === null ? <Loading /> :
       list.length === 0 ? <Empty title="조건에 맞는 인증 레코드가 없습니다." /> :
        <div style={{ display: "grid", gap: 8 }}>
          {list.map((t) => (
            <Card key={t.id}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {t.user.name} · {t.bureau.name}
                    &nbsp;<Badge tone={toneForTeacherStatus(t.status)}>{V.LABELS.teacherStatus[t.status]}</Badge>
                    {t.expired && <>&nbsp;<Badge tone="danger">만료</Badge></>}
                  </div>
                  <div style={{ fontSize: 12, color: TOKENS.ink3, marginTop: 2 }}>
                    {t.specialty && `전문분야: ${t.specialty} · `}
                    {t.certified_on ? `인증 ${fmt(t.certified_on)}` : "신청 접수"}
                    {t.expires_on && ` · 만료 ${fmt(t.expires_on)}`}
                    {t.certified_by && ` · 인증자: ${t.certified_by}`}
                  </div>
                  {t.statement && (
                    <div style={{ fontSize: 12, color: TOKENS.ink2, marginTop: 6, fontStyle: "italic" }}>
                      "{t.statement}"
                    </div>
                  )}
                </div>
                {isOperator && (
                  <div style={{ display: "flex", gap: 4 }}>
                    {t.status === "pending" && (
                      <Button size="sm" onClick={() => act(() => V.teachers.approve(t.id), "인증했습니다.")}>
                        인증
                      </Button>
                    )}
                    {t.status === "approved" && (
                      <Button variant="danger" size="sm"
                        onClick={() => {
                          if (!window.confirm(`${t.user.name}의 ${t.bureau.name} 교사 인증을 정지하시겠습니까?`)) return;
                          act(() => V.teachers.suspend(t.id), "정지했습니다.");
                        }}>정지</Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      }

      <ApplyTeacherModal
        open={applying}
        bureaus={bureaus}
        onClose={() => setApplying(false)}
        onApplied={() => { setApplying(false); reload(); }}
      />
    </div>
  );
}

function ApplyTeacherModal({ open, bureaus, onClose, onApplied }: {
  open: boolean; bureaus: V.Bureau[]; onClose: () => void; onApplied: () => void;
}) {
  const toast = useToast();
  const [bureauCode, setBureauCode] = React.useState<V.BureauCode>("L");
  const [specialty, setSpecialty] = React.useState("");
  const [statement, setStatement] = React.useState("");
  const [bio, setBio] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setBureauCode("L"); setSpecialty(""); setStatement(""); setBio(""); setSubmitting(false);
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await V.teachers.apply({
        bureau_code: bureauCode,
        specialty: specialty || undefined,
        bio: bio || undefined,
        statement: statement || undefined,
      });
      toast.push("인증 신청을 접수했습니다. 운영자 심사 후 인증됩니다.", "success");
      onApplied();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const activeBureaus = bureaus.filter((b) => b.kind === "bureau");

  return (
    <Modal
      open={open}
      title="마을교사 인증 신청"
      onClose={onClose}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>취소</Button>
          <Button onClick={submit} disabled={submitting}>{submitting ? "신청 중…" : "신청"}</Button>
        </>
      }
    >
      <Field label="담당 교육국" required>
        <Select value={bureauCode} onChange={(e) => setBureauCode(e.target.value as V.BureauCode)}>
          {activeBureaus.map((b) => (
            <option key={b.code} value={b.code}>{b.name}</option>
          ))}
        </Select>
      </Field>
      <Field label="전문 분야">
        <Input value={specialty} onChange={(e) => setSpecialty(e.target.value)} placeholder="예: 독서지도" />
      </Field>
      <Field label="교학상장 다짐 (statement)"
             hint="아이와 함께 배우겠다는 짧은 선언">
        <Textarea value={statement} onChange={(e) => setStatement(e.target.value)} />
      </Field>
      <Field label="약력 (bio)">
        <Textarea value={bio} onChange={(e) => setBio(e.target.value)} />
      </Field>
    </Modal>
  );
}

function toneForTeacherStatus(s: string): any {
  if (s === "approved") return "success";
  if (s === "pending")  return "warning";
  if (s === "suspended") return "danger";
  return "neutral";
}
function fmt(iso: string) { try { return new Date(iso).toLocaleDateString("ko-KR"); } catch { return iso; } }
